import { useEffect, useCallback, DependencyList } from 'react';
import hotkeys, { KeyHandler, HotkeysEvent } from 'hotkeys-js';

// @see https://github.com/jaywcjlove/hotkeys#filter
hotkeys.filter = () => true;

type Options = {
  scope?: string;
  element?: HTMLElement | null;
  keyup?: boolean | null;
  keydown?: boolean | null;
  splitKey?: string;
  preventDefault?: boolean;
};

// Signatures
export function useHotkeys(
  keys: string,
  callback: KeyHandler,
  options?: Options,
): void;
export function useHotkeys(
  keys: string,
  callback: KeyHandler,
  deps?: DependencyList,
): void;
export function useHotkeys(
  keys: string,
  callback: KeyHandler,
  options?: Options,
  deps?: DependencyList,
): void;

// Definition
export function useHotkeys(
  keys: string,
  callback: KeyHandler,
  options?: Options | DependencyList,
  deps?: DependencyList,
) {
  if (options instanceof Array) {
    deps = options;
    options = undefined;
  }

  deps = deps || [];
  options = options || {};
  const { preventDefault = true } = options;

  const handler = useCallback(
    (keyboardEvent: KeyboardEvent, hotkeysEvent: HotkeysEvent) => {
      if (preventDefault) {
        keyboardEvent.preventDefault();
      }
      // NOTE: the setTimeout below helps to asynchronously execute all the callbacks registered with the same hotkeys
      // so even if a re-render happens & cleanup/unbind the previous callbacks handlers, the callback will be executed
      setTimeout(() => callback(keyboardEvent, hotkeysEvent));
    },
    deps,
  );

  const cleanup = () => {
    hotkeys.unbind(keys, handler);
  };

  useEffect(() => {
    hotkeys(keys, options as Options, handler);
    return cleanup;
  }, [keys, options, handler]);
}
